// Copyright (c) Docugami, Inc. All rights reserved.

import { IconDescriptor, IconRenderer } from 'components/work_path/IconRenderer';
import { getClickEventIdForAutomationId } from 'telemetry/TelemetryEvents';
import { Telemetry } from 'telemetry/TelemetryManager';
import styled from 'styled-components';
import { homeGray, primaryTheme } from 'components/shell/presentation/AppStyles';
import { LimitsContainer, UploadLimitsContainer } from 'components/common/LimitsContainer';
import { ActionTilePreviewPill } from './PreviewPill';
import { getAutomationIdForActionTileId } from 'utils/Automation';
import { ActionTileId } from 'hooks/ActionTileItems';

const ActionTileContainer = styled.div`
    display: flex;
    flex-direction: column;
    position: relative;
    width: 180px;
    min-width: 180px;
    height: 180px;
    box-sizing: border-box;
    background: ${primaryTheme.palette.white};
    box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.2);
    border-radius: 2px;
    cursor: pointer;
    align-items: center;
    justify-content: center;

    :hover {
        transform: scale(1.02);
    }
`;

const ActionTileLabel = styled.span`
    font-family: 'Lato';
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    line-height: 17px;
    display: flex;
    align-items: center;
    text-align: center;
    color: ${homeGray};
    padding: 5px;
`;

export interface ActionTileProps {
    id: ActionTileId;
    iconInfo: IconDescriptor;
    actionText: string;
    onClick?: () => void;
    isPreview?: boolean;
    isPageLimited?: boolean;
    isLimited?: boolean;
}

export const ActionTile = ({ id, iconInfo, actionText, onClick, isPreview, isPageLimited, isLimited }: ActionTileProps): JSX.Element => {
    function onActionClick() {
        Telemetry.trackNamedEvent(getClickEventIdForAutomationId(getAutomationIdForActionTileId(id)));
        onClick?.();
    }

    const content = (
        <ActionTileContainer data-automation-id={getAutomationIdForActionTileId(id)} key={id} onClick={onActionClick} title={actionText}>
            {!!isPreview && <ActionTilePreviewPill />}
            <IconRenderer source={iconInfo} height={iconInfo.height * 100} width={iconInfo.width * 90} containerHeight={120} />
            <ActionTileLabel>{actionText}</ActionTileLabel>
        </ActionTileContainer>
    );

    if (isLimited) {
        return (
            <LimitsContainer actionName={`use the ${actionText.toLocaleUpperCase()} action`} >
                {content}
            </LimitsContainer>
        )
    }

    if (isPageLimited) {
        return (
            <UploadLimitsContainer>
                {content}
            </UploadLimitsContainer>
        )
    }

    return content;
}